import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { Tutor, TutorService } from '../../../../services/tutor.service';
import {
  OrganisationFeatures,
  OrganisationService,
} from '../../../../services/organisation.service';

/**
 * Reiter „Wissensbasis" von Agent › Wissen (/tutors/:id/knowledge/base).
 * Menü, Karte und Reiter stellt TutorKnowledgeComponent; hier nur Suche und
 * Inhalt. Die frühere eigene Seite /tutors/:id/knowledge-base leitet auf den
 * Reiter um.
 */
@Component({
  // Eigener Tag: `app-knowledge-base` ist die Seite Wissensbasis der
  // Organisation (gleiches AppModule).
  selector: 'app-tutor-knowledge-base',
  templateUrl: './knowledge-base.component.html',
  styleUrl: './knowledge-base.component.scss',
})
export class KnowledgeBaseComponent implements OnDestroy {
  tutor: Tutor | undefined;
  searchText = '';
  /**
   * Whether the lecturer feature is enabled for this tutor — drives
   * visibility of the second "Wissensbasis – Dozent" picker. Read from
   * `tutorSettings$` because lecturer_mode is a template_setting, not
   * a column on the bots table.
   */
  lecturerOn = false;
  /** Das Anhaken eben hat den Wissens-Modus auf „Nur Wissensbasis" gestellt. */
  modeSwitched = false;

  private destroy$ = new Subject<void>();

  constructor(
    protected tutorService: TutorService,
    protected activeRoute: ActivatedRoute,
    private organisationService: OrganisationService
  ) {
    // The agent shell (`tutors/:tutorId`) holds the id.
    const tutorId =
      this.activeRoute.snapshot.paramMap.get('tutorId') ??
      this.activeRoute.parent?.snapshot.paramMap.get('tutorId');
    this.tutorService.tutors$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.tutor = this.tutorService.findTutor(tutorId!);
      });
    this.tutorService.tutorSettings$
      .pipe(takeUntil(this.destroy$))
      .subscribe((settings) => {
        const s = settings.find((x) => x.variable_name === 'lecturer_mode');
        const v = s?.value ?? s?.default_value;
        this.lecturerOn = v === true || v === 'true';
      });
  }

  /** True when the active bot uses the alpha_dozent template (a.k.a.
   *  "Alpha Twin"). The KB picker copy is tailored for that case:
   *  the section subtitle is hidden and the hint explains that the
   *  Twin inherits course knowledge from its linked Tutor/Flexible
   *  bot, with optional augmentation by the lecturer's own uploads. */
  get isTwin(): boolean {
    if (!this.tutor?.template_id) return false;
    const template = this.tutorService.tutorTemplates$.value.find(
      (t) => t.id === this.tutor!.template_id
    );
    return !!template && template.name === 'alpha_dozent';
  }

  /** Erstes Wissen → al-tutor hat „Nur Wissensbasis" gesetzt: Hinweis zeigen
   *  und die Einstellungen neu laden. Ohne das stünde auf „Quellen & Suche"
   *  bis zum Neuladen noch „Uneingeschränkt" — und wer dort dann speichert,
   *  schickte den alten Wert als eigene Wahl zurück. Der neue Wert erreicht
   *  über `tutorSettings$` auch den Portal-Hinweis „Begrenze das KI-Wissen". */
  onKnowledgeModeSwitched(): void {
    this.modeSwitched = true;
    if (this.tutor?.id) {
      this.tutorService.getTutorSettings(this.tutor.id).subscribe({ error: () => undefined });
    }
  }

  /** Den Verweis auf „Platzierungen" nur zeigen, wenn es den Menüpunkt gibt. */
  get hasPlacements(): boolean {
    return this.organisationService.hasFeature(OrganisationFeatures.TUTOR_PLACEMENTS);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
